import { VoiceAgentFormData } from '../components/ConfigSteps.tsx';
import { CALLER_VERIFICATION_ATTEMPTS_DEFAULT, MAX_CALL_DURATION_DEFAULT, VOICE_SPEED_DEFAULT } from '../constants.ts';
import { editablePromptToInput } from './editablePrompt.ts';
import { normaliseEmailDomain, parseRecipientList } from './recipients.ts';

export const transformVoiceAgentFormData = (data: VoiceAgentFormData) => {
    return {
        name: data.name,
        companyName: data.name,
        language: data.language,
        introSpeech: data.introSpeech,
        introSpeechPersonalized: data.introSpeechPersonalized,
        consentQuestion: data.consentQuestion,
        phoneNumber: data.phoneNumber,
        transferPhoneNumber: data.transferPhoneNumber,
        fallbackEmail: data.fallbackEmail,
        sendEmailAllowedDomains: parseRecipientList(data.sendEmailAllowedDomains).map(normaliseEmailDomain),
        callerIdentificationLookupDefinitionId: data.callerIdentificationLookupDefinitionId,
        orderHistoryLookupDefinitionId: data.orderHistoryLookupDefinitionId,
        orderLookupDefinitionId: data.orderLookupDefinitionId,
        articleLookupDefinitionId: data.articleLookupDefinitionId,
        erpToolProviderKey: data.erpToolProviderKey,
        erpToolProviderIntegrationId: data.erpToolProviderIntegrationId,
        requireIdentifiedCaller: data.requireIdentifiedCaller,
        callerRejectionMessage: data.callerRejectionMessage,
        requireCallerVerification: data.requireCallerVerification,
        callerVerificationField: data.callerVerificationField,
        callerVerificationExtraDataKey: data.callerVerificationExtraDataKey,
        callerVerificationQuestion: data.callerVerificationQuestion,
        callerVerificationMaxAttempts:
            parseInt(data.callerVerificationMaxAttempts, 10) || CALLER_VERIFICATION_ATTEMPTS_DEFAULT,
        callerVerificationFailedMessage: data.callerVerificationFailedMessage,
        backgroundVoiceDetection: data.backgroundVoiceDetection,
        voiceModel: data.voiceModel,
        // the slider's number box can be cleared — fall back to the defaults instead of sending nothing
        voiceSpeed: Number.isFinite(data.voiceSpeed) ? data.voiceSpeed : VOICE_SPEED_DEFAULT,
        maxCallDurationSeconds: Number.isFinite(data.maxCallDurationSeconds)
            ? Math.round(data.maxCallDurationSeconds)
            : MAX_CALL_DURATION_DEFAULT,
        forwardOnConsentDenial: data.forwardOnConsentDenial,
        webCallRequestsEnabled: data.webCallRequestsEnabled,
        webCallAskConsentQuestion: data.webCallAskConsentQuestion,
        webCallAllowedPrefixes: (data.webCallAllowedPrefixes || '')
            .split(',')
            .map((prefix) => prefix.trim())
            .filter(Boolean),
        webCallDailyCap: parseInt(data.webCallDailyCap, 10) || 50,
        // 0 is a valid value (cooldown disabled) — only fall back to the default when unparsable
        webCallCooldownSeconds: Number.isNaN(parseInt(data.webCallCooldownSeconds, 10))
            ? 3600
            : parseInt(data.webCallCooldownSeconds, 10),
        calendlyMeetingLink: data.calendlyMeetingLink,
        calendlySummaryQuestion: data.calendlySummaryQuestion,
        // write-only secret — only send when the user entered a new token
        ...(data.calendlyApiToken ? { calendlyApiToken: data.calendlyApiToken } : {}),
        summaryEmailEnabled: data.summaryEmailEnabled,
        summaryEmailRecipients: parseRecipientList(data.summaryEmailRecipients),
        summaryTeamsEnabled: data.summaryTeamsEnabled,
        summaryTeamsTeamId: data.summaryTeamsTeamId,
        summaryTeamsTeamName: data.summaryTeamsTeamName,
        summaryTeamsChannelId: data.summaryTeamsChannelId,
        summaryTeamsChannelName: data.summaryTeamsChannelName,
        enabledTools: data.enabledTools,
        editablePrompt: editablePromptToInput(data.editablePrompt),
        defaultChannels:
            data.defaultChannels && (data.defaultChannels.orderChannelId || data.defaultChannels.rfqChannelId)
                ? {
                      orderChannelId: data.defaultChannels.orderChannelId || null,
                      rfqChannelId: data.defaultChannels.rfqChannelId || null,
                  }
                : null,
        documentFields: {
            order: data.documentFields?.order
                ? {
                      headerFields: data.documentFields.order.headerFields,
                      lineItemFields: data.documentFields.order.lineItemFields,
                  }
                : null,
            rfq: data.documentFields?.rfq
                ? {
                      headerFields: data.documentFields.rfq.headerFields,
                      lineItemFields: data.documentFields.rfq.lineItemFields,
                  }
                : null,
        },
        teamId: data.teamId || null,
    };
};
